import { useState } from 'react';
import type { RevenueByModelDailyResponse } from '@nadoutong/contracts';
import { usePricing } from '../../api/pricing.ts';
import { useRevenueByModel, useRevenueByModelDaily } from '../../api/revenue.ts';
import {
  TREND_TONE_CYCLE,
  TrendBucketTable,
  TrendChart,
  TrendViewToggle,
  rollupDailyBuckets,
} from '../../components/charts/TrendChart.tsx';
import type {
  TrendChartSeries,
  TrendRollup,
  TrendTableColumn,
  TrendView,
} from '../../components/charts/TrendChart.tsx';
import {
  formatCnyCents,
  usdMicrosFromCentsFallback,
  usdMicrosToCnyCents,
} from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, Panel, Skeleton, TBody, Table, Td, Th, THead, Tr } from '../../ui/index.ts';
import type { MetricsTabContext } from './hubState.ts';

/**
 * 模型 tab (epic #1643): top-N per-model daily charged trend
 * (`/by-model/daily`, top-8 by charged micros) + the per-model window
 * aggregate table (`/by-model`, page window).
 *
 * Cost caveat: `RevenueByModelRow` carries no per-row missing-cost
 * signal, so table cost/profit render the ledger sums as-is; the
 * aggregate `cost_missing_count` from the daily series is surfaced as
 * a warn chip so an understated margin is labelled rather than silent.
 */

const TREND_DAY_OPTIONS = [30, 60, 90] as const;
const TREND_MODEL_LIMIT = 8;
const COUNT_FORMATTER = new Intl.NumberFormat('en-US');

export function ModelsTab({ context }: { readonly context: MetricsTabContext }): JSX.Element {
  const t = useT();
  const { brand, range } = context;
  const [trendDays, setTrendDays] = useState<number>(30);
  const [trendView, setTrendView] = useState<TrendView>('chart');
  const [trendRollup, setTrendRollup] = useState<TrendRollup>('day');
  // #1683: click-to-toggle legend on the multi-line chart (chart only —
  // the table twin keeps all top-N columns; component state, not URL).
  const [hiddenModels, setHiddenModels] = useState<ReadonlySet<string>>(new Set());
  const toggleModel = (key: string): void => {
    setHiddenModels((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };
  const trend = useRevenueByModelDaily({ days: trendDays, brand, limit: TREND_MODEL_LIMIT });
  const table = useRevenueByModel({ brand, from: range.from, to: range.to });
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const rateValid = typeof cnyPerUsd === 'number' && Number.isFinite(cnyPerUsd) && cnyPerUsd > 0;
  const chartSeries =
    trend.data !== undefined && rateValid ? buildModelSeries(trend.data, cnyPerUsd) : [];
  const trendLabels = trend.data?.series[0]?.days.map((day) => day.date) ?? [];
  const costMissingCount =
    trend.data === undefined
      ? 0
      : trend.data.series.reduce(
          (sum, series) => sum + series.days.reduce((daySum, day) => daySum + day.cost_missing_count, 0),
          0,
        );

  return (
    <div className="flex flex-col gap-6" data-testid="metrics-models-tab">
      <Panel
        caption={t('metrics.models.trend.title')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div role="tablist" aria-label={t('metrics.trend.days_aria')} className="inline-flex gap-1">
              {TREND_DAY_OPTIONS.map((option) => {
                const active = option === trendDays;
                return (
                  <Button
                    key={option}
                    role="tab"
                    aria-selected={active}
                    variant={active ? 'secondary' : 'ghost'}
                    size="sm"
                    className={active ? 'bg-active text-paper' : undefined}
                    data-testid={`metrics-models-trend-days-${option}`}
                    onClick={() => setTrendDays(option)}
                  >
                    {t('metrics.trend.days', { days: option })}
                  </Button>
                );
              })}
            </div>
            <TrendViewToggle
              view={trendView}
              onViewChange={setTrendView}
              rollup={trendRollup}
              onRollupChange={setTrendRollup}
              testIdPrefix="metrics-models-trend"
            />
          </div>
        }
        padded
        testId="metrics-models-trend"
      >
        {trend.isPending ? (
          <Skeleton className="h-56" testId="metrics-models-trend-loading" />
        ) : trend.isError ? (
          <p className="m-0 text-body text-bad-bright" data-testid="metrics-models-trend-error">
            {t('metrics.models.trend.error')}
          </p>
        ) : trend.data !== undefined ? (
          <div className="flex flex-col gap-3">
            {rateValid ? (
              trendView === 'chart' ? (
                <TrendChart
                  labels={trendLabels}
                  series={chartSeries}
                  hiddenKeys={hiddenModels}
                  onToggleSeries={toggleModel}
                  valueFormatter={(value) => formatCnyCents(value, value < 0)}
                  ariaLabel={t('metrics.models.trend.title')}
                  emptyLabel={t('metrics.models.trend.empty')}
                  testId="metrics-models-trend-chart"
                />
              ) : (
                <ModelsTrendTable labels={trendLabels} series={chartSeries} rollup={trendRollup} />
              )
            ) : (
              // Distinct from the empty state: the data is there, the
              // display rate is not — say so instead of faking "no data".
              <p
                className="m-0 rounded-lg border border-warn-line bg-warn-dim/30 p-4 text-body text-warn"
                data-testid="metrics-models-trend-rate-unavailable"
              >
                {t('metrics.models.trend.rate_unavailable')}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {trend.data.model_count > trend.data.series.length ? (
                <p className="m-0 text-micro text-paper-low" data-testid="metrics-models-trend-truncated">
                  {t('metrics.models.trend.truncated', {
                    shown: trend.data.series.length,
                    total: trend.data.model_count,
                  })}
                </p>
              ) : null}
              {/* Scope caveat: this count is summed over the TREND window
                  (days toggle × top-8), not the aggregate table's page
                  window — the copy names the scope so it can't be read as
                  covering the table below. */}
              {costMissingCount > 0 ? (
                <Chip tone="warn" testId="metrics-models-cost-missing">
                  {t('metrics.models.cost_missing', { count: costMissingCount })}
                </Chip>
              ) : null}
            </div>
          </div>
        ) : null}
      </Panel>

      <Panel
        caption={t('metrics.models.table.title')}
        actions={<span className="font-mono text-micro text-paper-low">{t('metrics.models.table.sub')}</span>}
        testId="metrics-models-table"
      >
        {table.isPending ? (
          <Skeleton className="m-4 h-40" testId="metrics-models-table-loading" />
        ) : table.isError ? (
          <p className="m-0 p-4 text-body text-bad-bright" data-testid="metrics-models-table-error">
            {t('metrics.models.table.error')}
          </p>
        ) : table.data === undefined || table.data.models.length === 0 ? (
          <p className="m-0 p-4 text-body text-paper-low" data-testid="metrics-models-table-empty">
            {t('metrics.models.table.empty')}
          </p>
        ) : (
          <>
            <Table className="border-none">
              <THead>
                <Tr interactive={false}>
                  <Th>{t('metrics.models.col.model')}</Th>
                  <Th num>{t('metrics.models.col.calls')}</Th>
                  <Th num>{t('metrics.models.col.charged')}</Th>
                  <Th num>{t('metrics.models.col.cost')}</Th>
                  <Th num>{t('metrics.models.col.profit')}</Th>
                  <Th num>{t('metrics.models.col.margin')}</Th>
                </Tr>
              </THead>
              <TBody>
                {table.data.models.map((row) => (
                  <Tr key={`${row.model_vendor}:${row.model_name}`}>
                    <Td>
                      <div className="font-mono font-medium text-paper">{row.model_name}</div>
                      <div className="text-micro text-paper-low">{row.model_vendor}</div>
                    </Td>
                    <Td num>{COUNT_FORMATTER.format(row.usage_count)}</Td>
                    <Td num className="text-ok">{formatCnyCents(row.cny_charged_cents, false)}</Td>
                    <Td num className="text-bad">{formatCnyCents(row.cny_upstream_cost_cents, false)}</Td>
                    <Td num className="whitespace-nowrap">{formatCnyCents(row.l2_profit_cny_cents, true)}</Td>
                    <Td num>{`${(row.l2_margin_pct * 100).toFixed(1)}%`}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
            {/* The ledger sums have no per-row missing-cost signal
                (`RevenueByModelRow`), so state the accounting basis
                explicitly instead of implying full cost coverage. */}
            <p
              className="m-0 border-t border-line-soft px-4 py-2.5 text-micro text-paper-low"
              data-testid="metrics-models-table-cost-note"
            >
              {t('metrics.models.table.cost_note')}
            </p>
          </>
        )}
      </Panel>
    </div>
  );
}

/**
 * The 表格 twin of the top-N model trend chart (#1675): one row per
 * bucket, one charged-CNY column per displayed model plus a 合计
 * column, rolled up client-side to ISO weeks or UTC+8 calendar months
 * on demand. Only rendered with a validated display rate — the caller
 * shows the rate-unavailable note otherwise (same rule as the chart).
 */
function ModelsTrendTable({
  labels,
  series,
  rollup,
}: {
  readonly labels: readonly string[];
  readonly series: readonly TrendChartSeries[];
  readonly rollup: TrendRollup;
}): JSX.Element {
  const t = useT();
  const rolled = rollupDailyBuckets(
    labels,
    series.map((s) => s.values),
    rollup,
  );
  const modelColumns: readonly TrendTableColumn[] = series.map((s, seriesIndex) => ({
    key: s.key,
    header: s.label,
    cells: rolled.labels.map((_, rowIndex) => formatCnyCents(rolled.series[seriesIndex]?.[rowIndex] ?? 0, false)),
  }));
  const totalColumn: TrendTableColumn = {
    key: 'total',
    header: t('metrics.models.table.col.total'),
    cells: rolled.labels.map((_, rowIndex) =>
      formatCnyCents(rolled.series.reduce((sum, values) => sum + (values[rowIndex] ?? 0), 0), false),
    ),
  };
  return (
    <TrendBucketTable
      bucketHeader={t('metrics.chart.col.bucket')}
      labels={rolled.labels}
      columns={[...modelColumns, totalColumn]}
      emptyLabel={t('metrics.models.trend.empty')}
      note={rollup === 'day' ? undefined : t('metrics.chart.rollup.note')}
      testId="metrics-models-trend-table"
    />
  );
}

/**
 * One line per top-N model. Values are per-day USD charged converted
 * to display CNY cents (the mockup renders ¥). The caller only invokes
 * this with a validated rate (it renders an explicit rate-unavailable
 * note otherwise); the internal null-check is a defensive fallback so
 * a bad conversion can never render ¥0 lines.
 */
function buildModelSeries(
  data: RevenueByModelDailyResponse,
  cnyPerUsd: number | null,
): readonly TrendChartSeries[] {
  const out: TrendChartSeries[] = [];
  for (const [index, series] of data.series.entries()) {
    const values: number[] = [];
    for (const day of series.days) {
      const cnyCents = usdMicrosToCnyCents(
        usdMicrosFromCentsFallback(day.usd_charged_micros, day.usd_charged_cents),
        cnyPerUsd,
      );
      if (cnyCents === null) {
        return [];
      }
      values.push(cnyCents);
    }
    out.push({
      key: `${series.model_vendor}/${series.model_name}`,
      label: series.model_name,
      kind: 'line',
      tone: TREND_TONE_CYCLE[index % TREND_TONE_CYCLE.length] ?? 'neutral',
      values,
    });
  }
  return out;
}
